import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixSocialGroupsApiV2JoinGroupRequestsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/join-proxy',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
      {
        srcPath: '/_api/join-proxy',
        destPath: '',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    'groups.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
      {
        srcPath: '/social-groups-proxy/api/join',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/join',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/social-groups-proxy/join',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_groups_join-group-requests';

/**
 * Approves pending join group requests.
 * Group managers always have access to this functionality. In some cases, site owners will allow group members to use this functionality as well.
 */
export function approveJoinGroupRequests(
  payload: object
): RequestOptionsFactory<any> {
  function __approveJoinGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.join_group_request',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.JoinGroupRequestsService.ApproveJoinGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2JoinGroupRequestsServiceUrl({
        protoPath: '/v2/groups/{groupId}/join-requests/approve',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'joinGroupRequests.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __approveJoinGroupRequests;
}

/**
 * Rejects pending join group requests.
 * Group managers always have access to this functionality. In some cases, site owners will allow group members to use this functionality as well.
 */
export function rejectJoinGroupRequests(
  payload: object
): RequestOptionsFactory<any> {
  function __rejectJoinGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.join_group_request',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.JoinGroupRequestsService.RejectJoinGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2JoinGroupRequestsServiceUrl({
        protoPath: '/v2/groups/{groupId}/join-requests/reject',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'joinGroupRequests.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __rejectJoinGroupRequests;
}

/**
 * Lists requests to join a group.
 *
 * > **Note:** This function is only relevant for private groups.
 *
 * The `listjoinGroupRequests()` function returns a Promise that resolves to a list of up to 100 requests to join a group. Sorts by default to `_createdDate` in descending order.
 * Only group admins can see requests to join their group. Site members can access their own join requests in their site.
 *
 * > **Notes:**
 * <!-- > + If the `suppressAuth` option is set to `true`, all permissions are overwritten, and all site members (including non-group members) can see requests to join a group. -->
 */
export function listJoinGroupRequests(
  payload: object
): RequestOptionsFactory<any> {
  function __listJoinGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.join_group_request',
      method: 'GET' as any,
      methodFqn:
        'wix.social.groups.api.v2.JoinGroupRequestsService.ListJoinGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2JoinGroupRequestsServiceUrl({
        protoPath: '/v2/groups/{groupId}/join-requests',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'joinGroupRequests.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __listJoinGroupRequests;
}

/**
 * Creates a query to retrieve a list of join requests.
 *
 * > **Notes:**
 * > + This function is only relevant for private groups.
 * > + For `SECRET` groups, only group admins can query requests to join their group. <!-- However, if the `suppressAuth` option is set to `true`, all permissions are overwritten, and all site members (including non-group members) can query requests to join a group. -->
 *
 * The `queryjoinGroupRequests()` function builds a query to retrieve a list of all requests to join a group, and returns a `JoinGroupRequestsQueryBuilder` object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](/join-group-requests-query-builder/find) function.
 *
 * You can refine the query by chaining `joinGroupRequestsQueryBuilder` functions onto the query. `joinGroupRequestsQueryBuilder` functions enable you to sort, filter, and control the results that `queryjoinGroupRequests()` returns.
 *
 * The results of the `queryjoinGroupRequests()` function are sorted by `_createdDate` in descending order.
 *
 * `queryjoinGroupRequests()` runs with this `joinGroupRequestsQueryBuilder` default, which you can override:
 * + [`limit(100)`](/join-group-requests-query-builder/limit)
 *
 * The following `joinGroupRequestsQueryBuilder` functions are supported for `queryjoinGroupRequests()`. For a full description of the joinGroupRequests object, see the object returned for the [`items`](/join-group-requests-query-result/items) property in `JoinGroupRequestsQueryResult`.
 *
 */
export function queryJoinGroupRequests(
  payload: object
): RequestOptionsFactory<any> {
  function __queryJoinGroupRequests({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.join_group_request',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.JoinGroupRequestsService.QueryJoinGroupRequests',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2JoinGroupRequestsServiceUrl({
        protoPath: '/v2/groups/{groupId}/join-requests/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'joinGroupRequests.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __queryJoinGroupRequests;
}
